import { useCallback, useEffect, useRef, useState } from "react";

import {
  Pause as PauseIcon,
  Play as PlayIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import { createStyles } from "@mantine/core";
import WaveSurfer from "wavesurfer.js";

export const useStyles = createStyles((theme) => ({
  audio: {
    objectFit: "cover",
    width: "100%",
    zIndex: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.fn.largerThan("md")]: {
      flexDirection: "row",
      alignItems: "flex-start",
    },
  },
  image: {
    position: "relative",
    height: 280,
    width: 280,
    flexShrink: 0,
    flexGrow: 0,
  },
  waveform: {
    height: 280,
    width: "100%",
    minWidth: "100%",
    [theme.fn.largerThan("md")]: {
      minWidth: "calc(100% - 320px)",
      maxWidth: 576,
    },
  },
  buttonIcon: {
    width: 30,
    height: 30,
  },
}));

export const AudioArtwork = ({
  animationUrl,
  imageUrl,
  onLoad,
  type = "mint",
}: {
  animationUrl: string;
  imageUrl: string;
  onLoad?: () => void;
  type?: string;
}) => {
  const { classes } = useStyles();

  const [audioPlaying, setAudioPlaying] = useState<boolean>(false);

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const waveformRef = useRef<any>(null);
  const onAudioButtonClick = () => {
    if (audioPlaying) {
      waveformRef.current.pause();
    } else {
      waveformRef.current.play();
    }

    setAudioPlaying(!audioPlaying);
  };

  const setWaveformRef = useCallback(() => {
    if (animationUrl && !waveformRef.current) {
      waveformRef.current = WaveSurfer.create({
        container: `#waveform-${type}`,
        height: 280,
        fillParent: true,
        barWidth: 3,
        hideScrollbar: true,
      });
      waveformRef.current.load(animationUrl);
    }
  }, [animationUrl]);

  useEffect(() => {
    if (onLoad) {
      onLoad();
    }
    return () => {
      waveformRef.current?.pause();
    };
  }, [onLoad]);

  return (
    <div className={classes.audio}>
      <div className={classes.image}>
        <ResizedImage
          src={imageUrl}
          fit="scale-down"
          height={280}
          width={280}
          style={{
            position: "absolute",
            filter: "blur(0)",
            zIndex: 1,
            marginRight: 100,
          }}
        />

        <button
          onClick={onAudioButtonClick}
          color="white"
          style={{
            zIndex: 10,
            borderRadius: "100%",
            border: "none",
            width: 70,
            height: 70,
            position: "absolute",
            top: 105,
            left: 105,
            paddingTop: 7,
            paddingLeft: 7,
          }}
        >
          {audioPlaying ? (
            <PauseIcon className={classes.buttonIcon} />
          ) : (
            <PlayIcon className={classes.buttonIcon} />
          )}
        </button>
      </div>
      <div
        id={`waveform-${type}`}
        ref={setWaveformRef}
        className={classes.waveform}
      ></div>
    </div>
  );
};
